import { forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from 'react'
import { cn } from '@/lib/utils.ts'

/**
 * Leichtgewichtige statische Tabelle. Fuer Micro-Listen (≤ 10 Zeilen, keine
 * Interaktion). Alles darueber: `DataTable` verwenden.
 *
 * API-Idee: man komponiert <SimpleTable>, <SimpleTable.Head>, <SimpleTable.Body>,
 * <SimpleTable.Tr>, <SimpleTable.Th>, <SimpleTable.Td>.
 */
const Root = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn('w-full text-small border-separate border-spacing-0', className)}
      {...props}
    />
  ),
)
Root.displayName = 'SimpleTable'

const Head = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn(
        'bg-gray-50 text-caption font-semibold uppercase tracking-wide text-gray-500',
        className,
      )}
      {...props}
    />
  ),
)
Head.displayName = 'SimpleTable.Head'

const Body = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn('text-gray-800', className)} {...props} />
  ),
)
Body.displayName = 'SimpleTable.Body'

const Tr = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr ref={ref} className={cn('bg-white', className)} {...props} />
  ),
)
Tr.displayName = 'SimpleTable.Tr'

const Th = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'text-left px-3 py-2 align-middle border-b border-gray-200 font-semibold',
        className,
      )}
      {...props}
    />
  ),
)
Th.displayName = 'SimpleTable.Th'

const Td = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('px-3 py-2 align-middle border-b border-gray-100', className)}
      {...props}
    />
  ),
)
Td.displayName = 'SimpleTable.Td'

const Compound = Object.assign(Root, { Head, Body, Tr, Th, Td })

export { Compound as SimpleTable }
